import { customQueries } from 'src/graphql/custom-queries'
import * as queries from 'src/graphql/queries'
import type { ReduxProjects, ReduxRecapInfo } from 'src/types/apiType'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import { getErrorMessage } from 'src/utils/error'
import { captureExceptionAndThrow } from 'src/utils/posthogException'

export async function fetchPaginatedListGraphqlApi({
	queryName,
	apiName = undefined,
	data,
}: {
	queryName: keyof typeof customQueries
	apiName?: string
	data: any
}) {
	let counter = 0
	try {
		const responseData = [] as any
		async function paginateQuery(
			nextToken: string | null = null
		): Promise<void> {
			if (counter > 0 && nextToken === null) {
				return
			}
			const response = (await API.graphql(
				graphqlOperation(customQueries[queryName], {
					...data,
					nextToken,
				})
			)) as any
			const queryData = response.data[apiName ?? queryName]?.items
			nextToken = response.data[apiName ?? queryName]?.nextToken
			counter++
			responseData.push(...queryData)
			return paginateQuery(nextToken)
		}
		await paginateQuery()
		return responseData
	} catch (err: any) {
		console.error('err while fetching', err)
		console.log('queryName err: ', queryName)
		console.log('data err: ', data)
		const requestSummary = {
			location: 'fetchPaginatedListGraphqlApi',
			queryName,
			apiName,
			pagesFetched: counter,
			payloadKeys: Object.keys(data ?? {}),
		}
		return captureExceptionAndThrow(err, requestSummary)
	}
}

export async function getSingularDataGraphqlApi({
	queryName,
	apiName = undefined,
	data,
}: {
	queryName: keyof typeof customQueries
	apiName?: string
	data: any
}) {
	try {
		const response = (await API.graphql(
			graphqlOperation(customQueries[queryName], { ...data })
		)) as any
		const responseData = response.data[apiName ?? queryName]
		return responseData
	} catch (err: any) {
		console.error('err while fetching', err)
		console.log('queryName err: ', queryName)
		console.log('data err: ', data)
	}
}

export async function getRecapInfoCostline(
	id: string
): Promise<{ data?: ReduxRecapInfo; error?: string }> {
	try {
		const {
			data: { getRecapInfo },
		} = (await API.graphql(
			graphqlOperation(queries.getRecapInfo, {
				id,
			})
		)) as {
			data: { getRecapInfo: ReduxRecapInfo }
		}
		return { data: getRecapInfo, error: undefined }
	} catch (error) {
		console.error('error in fetching recapInfo: ', error)
		const errMessage = getErrorMessage(error)
		return { data: undefined, error: errMessage }
	}
}

export async function fetchSubTeamProjectsByTenantIdUtil(tenantId: string) {
	const projectsArray = [] as ReduxProjects[]
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchProjects = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const data = (await API.graphql(
			graphqlOperation(queries.projectsByTenantId, {
				nextToken,
				tenantId,
				filter: {
					and: [
						{ activeFlag: { eq: true } },
						{ isSubTeam: { eq: true } },
					],
				},
				limit: 1000,
			})
		)) as {
			data: {
				projectsByTenantId: {
					items: ReduxProjects[]
					nextToken: string | null
				}
			}
		}
		const projects = data.data.projectsByTenantId.items
		const newNextToken = data.data.projectsByTenantId.nextToken
		counter++
		projectsArray.push(...projects)
		return paginateFetchProjects(newNextToken)
	}
	await paginateFetchProjects()
	return projectsArray
}

export async function fetchSubTeamProjectsByTenantIdUtilForTeamAdmin({
	tenantId,
	teamIds,
}: {
	tenantId: string
	teamIds: string[]
}) {
	const projectsArray = [] as ReduxProjects[]
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchProjects = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const data = (await API.graphql(
			graphqlOperation(queries.projectsByTenantId, {
				nextToken,
				tenantId,
				filter: {
					and: [
						{ activeFlag: { eq: true } },
						{ isSubTeam: { eq: true } },
					],
				},
				limit: 1000,
			})
		)) as {
			data: {
				projectsByTenantId: {
					items: ReduxProjects[]
					nextToken: string | null
				}
			}
		}
		const projects = data.data.projectsByTenantId.items
		const newNextToken = data.data.projectsByTenantId.nextToken
		counter++
		const filteredProjects = projects.filter((project) => {
			return teamIds?.includes(project.parentTeamId ?? '')
		})
		projectsArray.push(...filteredProjects)
		return paginateFetchProjects(newNextToken)
	}
	await paginateFetchProjects()
	return projectsArray
}
